import type { AuthPort, AuthState } from "../../desktop-next/src/auth-port.ts";
import { BrowserAccountAuth } from "./platform/account-auth.ts";
import { BrowserAuth } from "./platform/browser-auth.ts";
import type { CloudAuthChallengePurpose, CloudAuthConfig, CloudEmailChallenge, CloudExternalLoginOptions, CloudExternalProvider } from "@orni/sdk-typescript";
import {
  formatCloudNetworkError,
  normalizeCloudOrigin,
  resolveCloudRequestUrl,
} from "./cloud-fetch.ts";

const environment = (import.meta as ImportMeta & {
  env?: Record<string, unknown>;
}).env ?? {};
const configuredCloudOrigin = typeof environment.VITE_ORNI_CLOUD_ORIGIN === "string"
  ? environment.VITE_ORNI_CLOUD_ORIGIN.trim()
  : "";
const cloudOrigin = normalizeCloudOrigin(configuredCloudOrigin);
// During Vite development, relative requests intentionally use the local
// proxy. Production builds use the configured absolute origin when present.
const useDevProxy = environment.DEV === true || environment.MODE === "development";

const cloudFetcher: typeof fetch = async (input, init): Promise<Response> => {
  const value = input instanceof Request ? input.url : String(input);
  const requestUrl = resolveCloudRequestUrl(value, { cloudOrigin, useDevProxy });
  try {
    return await globalThis.fetch(requestUrl, init);
  } catch (cause) {
    throw new Error(formatCloudNetworkError(requestUrl, cause, { useDevProxy }), { cause });
  }
};

/** Browser cookie composition for the shared Desktop UI. */
export class CloudWebAuth implements AuthPort {
  private readonly browser = new BrowserAuth({ fetch: cloudFetcher });
  private readonly account = new BrowserAccountAuth(cloudFetcher);
  private stateValue: AuthState = "signed_out";
  private readonly listeners = new Set<(state: AuthState) => void>();

  get state(): AuthState { return this.stateValue; }

  async initialize(): Promise<void> {
    await this.browser.initialize();
    this.syncBrowserState(this.browser.state);
  }

  /** Shared single-flight cookie renewal for every Cloud data transport. */
  recoverUnauthorized(): Promise<boolean> {
    return this.browser.recoverUnauthorized();
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    listener(this.stateValue);
    const unsubscribe = this.browser.subscribe((state) => this.syncBrowserState(state));
    return () => { this.listeners.delete(listener); unsubscribe(); };
  }

  dispose(): void { this.listeners.clear(); }
  async capabilities(): Promise<CloudAuthConfig> {
    const capabilities = await this.account.capabilities();
    return { ...capabilities, accessTokenTtlSeconds: 900 };
  }
  accountInfo(): Promise<{ email: string; passwordConfigured: boolean }> {
    return this.account.accountInfo();
  }
  requestEmailCode(email: string, purpose: CloudAuthChallengePurpose = "sign_in"): Promise<CloudEmailChallenge> {
    return this.account.requestEmailCode(email, purpose) as Promise<CloudEmailChallenge>;
  }
  async verifyEmailCode(input: { challengeId: string; email: string; code: string }): Promise<void> {
    await this.account.verifyEmailCode(input);
    this.browser.markAuthenticated();
    await this.initialize();
    this.update("authenticated");
  }
  async loginWithPassword(input: { email: string; password: string }): Promise<void> {
    await this.account.passwordLogin(input);
    this.browser.markAuthenticated();
    await this.initialize();
    this.update("authenticated");
  }
  setPassword(input: { currentPassword?: string; password: string }): Promise<void> {
    return this.account.setPassword(input);
  }
  async verifyPasswordReset(input: { challengeId: string; email: string; code: string }): Promise<void> {
    await this.account.verifyPasswordReset(input);
  }
  async completePasswordReset(input: { password: string }): Promise<void> {
    await this.account.completePasswordReset(input);
    this.browser.markAuthenticated();
    await this.initialize();
    this.update("authenticated");
  }
  openBrowserLogin(): Promise<void> { return this.browser.signIn(); }
  async completeBrowserLogin(_input: { code: string; state: string }): Promise<void> { await this.initialize(); }
  startExternalLogin(provider: CloudExternalProvider, options: CloudExternalLoginOptions = {}): void {
    const path = this.account.externalLoginUrl(provider as "google" | "github", { returnTo: window.location.href, ...options });
    const url = resolveCloudRequestUrl(path, { cloudOrigin, useDevProxy });
    window.location.assign(url);
  }
  async signOut(): Promise<void> { await this.browser.signOut(); this.update("signed_out"); }

  private update(state: AuthState): void {
    if (this.stateValue === state) return;
    this.stateValue = state;
    for (const listener of this.listeners) listener(state);
  }

  /**
   * A browser auth check can be temporarily unavailable (for example while a
   * mobile/desktop tab is offline).  `BrowserAuth` deliberately distinguishes
   * that state from an explicit signed-out/expired-cookie result.  Preserve a
   * previously authenticated shell during the transient outage so Cloud Web
   * does not replace the session list with the login form and make the user
   * sign in again.  An actual 401/403 refresh still emits `signed_out`.
   */
  private syncBrowserState(
    state: "checking" | "signed_out" | "authenticated" | "unavailable",
  ): void {
    if (state === "unavailable" && this.stateValue === "authenticated") return;
    this.update(mapBrowserState(state));
  }
}

// The Cloud Web shell and its shared Desktop client must use the same request
// boundary. In Vite development that boundary is the 4195 proxy, which keeps
// the browser cookie on one origin while forwarding to the local Cloud port.
// Production builds resolve requests directly against the configured Cloud
// origin.
export { cloudFetcher, cloudOrigin, useDevProxy };

function mapBrowserState(state: "checking" | "signed_out" | "authenticated" | "unavailable"): AuthState {
  return state === "authenticated" ? "authenticated" : state === "unavailable" ? "signed_out" : "signed_out";
}
